const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(', ');

const stateByDocument = new WeakMap();

function getDocumentState(documentRef) {
    let state = stateByDocument.get(documentRef);
    if (state !== undefined) {
        return state;
    }

    state = { stack: [] };
    stateByDocument.set(documentRef, state);
    documentRef.addEventListener('keydown', (event) => {
        const activeScope = state.stack[state.stack.length - 1];
        if (activeScope === undefined) {
            return;
        }

        if (event.key === 'Escape') {
            // An open combobox list inside the dialog closes first; the next Escape closes the modal.
            const target = event.target;
            if (
                target?.getAttribute?.('role') === 'combobox'
                && target.getAttribute('aria-expanded') === 'true'
                && activeScope.dialog.contains(target)
            ) {
                return;
            }

            event.preventDefault();
            event.stopImmediatePropagation();
            activeScope.requestClose();

            return;
        }

        if (event.key === 'Tab') {
            activeScope.trapFocus(event);
        }
    }, { capture: true });

    return state;
}

function getFocusableElements(dialog) {
    return Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter((node) => {
        if (
            !(node instanceof HTMLElement)
            || node.hidden
            || node.getAttribute('aria-hidden') === 'true'
            || node.tabIndex < 0
            || node.closest?.('[hidden], [aria-hidden="true"]') !== null
        ) {
            return false;
        }

        return typeof node.getClientRects !== 'function' || node.getClientRects().length > 0;
    });
}

function canRestoreFocus(element) {
    if (!(element instanceof HTMLElement) || !element.isConnected) {
        return false;
    }

    if (element.closest?.('[hidden], [aria-hidden="true"]') !== null) {
        return false;
    }

    return typeof element.getClientRects !== 'function' || element.getClientRects().length > 0;
}

export function createModalFocusScope({
    dialog,
    requestClose,
    documentRef = globalThis.document,
}) {
    if (
        !(dialog instanceof HTMLElement)
        || typeof requestClose !== 'function'
        || typeof documentRef?.addEventListener !== 'function'
    ) {
        return null;
    }

    const state = getDocumentState(documentRef);
    let restoreFocus = null;
    let active = false;

    const focus = (preferredElement = null) => {
        if (!active || state.stack[state.stack.length - 1] !== controller) {
            return;
        }

        if (preferredElement instanceof HTMLElement && dialog.contains(preferredElement)) {
            preferredElement.focus();

            return;
        }

        const firstFocusable = getFocusableElements(dialog)[0];
        if (firstFocusable instanceof HTMLElement) {
            firstFocusable.focus();

            return;
        }

        dialog.focus();
    };

    const activate = (trigger = documentRef.activeElement) => {
        if (active) {
            return;
        }

        restoreFocus = trigger instanceof HTMLElement ? trigger : null;
        active = true;
        state.stack.push(controller);
        documentRef.body?.classList?.add('ui-modal-open');
    };

    // `restoreFocus: false` leaves focus to the caller (e.g. a successor chosen after the content changes).
    const deactivate = ({ restoreFocus: shouldRestoreFocus = true } = {}) => {
        if (!active) {
            return;
        }

        const index = state.stack.indexOf(controller);
        const wasTopmost = index === state.stack.length - 1;
        if (index !== -1) {
            state.stack.splice(index, 1);
        }
        active = false;

        if (state.stack.length === 0) {
            documentRef.body?.classList?.remove('ui-modal-open');
        }

        if (!wasTopmost || !shouldRestoreFocus) {
            restoreFocus = null;

            return;
        }

        const nextScope = state.stack[state.stack.length - 1];
        if (
            canRestoreFocus(restoreFocus)
            && (nextScope === undefined || nextScope.dialog.contains(restoreFocus))
        ) {
            restoreFocus.focus();
        } else {
            nextScope?.focus();
        }
        restoreFocus = null;
    };

    const trapFocus = (event) => {
        const focusableElements = getFocusableElements(dialog);
        if (focusableElements.length === 0) {
            event.preventDefault();
            dialog.focus();

            return;
        }

        const first = focusableElements[0];
        const last = focusableElements[focusableElements.length - 1];
        const activeElement = documentRef.activeElement;
        const outsideDialog = !dialog.contains(activeElement);
        if (event.shiftKey) {
            if (activeElement === first || outsideDialog) {
                event.preventDefault();
                last.focus();
            }

            return;
        }

        if (activeElement === last || outsideDialog) {
            event.preventDefault();
            first.focus();
        }
    };

    const controller = {
        activate,
        deactivate,
        dialog,
        focus,
        requestClose,
        trapFocus,
    };

    return controller;
}
